import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaRupeeSign, FaCalendarAlt, FaChartLine, FaDownload, FaSearch, FaUndo } from 'react-icons/fa';
import './adminreportspage.css';
import AdminPageLoader from './AdminPageLoader';
const API_BASE_URL = process.env.REACT_APP_API_URL;

const AdminReportPage = () => {
    const [salesSummary, setSalesSummary] = useState(null);
    const [dailySales, setDailySales] = useState([]);
    const [monthlySales, setMonthlySales] = useState([]);
    const [customSales, setCustomSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    const [downloading, setDownloading] = useState(false);
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [summaryRes, dailyRes, monthlyRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/sales-summary`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/daily-sales`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/monthly-sales`, { headers: { Authorization: `Bearer ${token}` } })
                ]);
                setSalesSummary(summaryRes.data);
                setDailySales(dailyRes.data);
                setMonthlySales(monthlyRes.data);
            } catch (err) {
                alert('Failed to fetch sales data.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [API_BASE_URL, token]);

    const fetchCustomSales = async () => {
        if (!dateRange.from || !dateRange.to) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/all-orders`, { headers: { Authorization: `Bearer ${token}` } });
            // Filter orders by date range and delivered only
            const fromDate = new Date(dateRange.from);
            const toDate = new Date(dateRange.to);
            toDate.setHours(23, 59, 59, 999);
            const filtered = (res.data || []).filter(order => {
                const created = new Date(order.createdAt);
                return created >= fromDate && created <= toDate && order.status === 'delivered';
            });
            setCustomSales(filtered);
        } catch (err) {
            alert('Failed to fetch custom sales data.');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (e) => {
        setDateRange({ ...dateRange, [e.target.name]: e.target.value });
    };

    const handleReset = () => {
        setDateRange({ from: '', to: '' });
        setCustomSales([]);
    };

    const handleDownloadCSV = () => {
        setDownloading(true);
        let rows = [
            ['Order ID', 'Date', 'Amount (₹)', 'Status', 'Mobile', 'Paid Status'],
            ...customSales.map(order => [
                `BEST-${order._id.slice(-5)}`,
                '"' + new Date(order.createdAt).toLocaleString('en-IN') + '"',
                order.totalAmount,
                order.status,
                order.mobile,
                order.paymentStatus === 'paid' ? (order.paymentMethod === 'paynow' ? 'Online' : order.paymentMethod === 'cod' ? 'Offline' : order.paymentMethod) : order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)
            ])
        ];
        // Add total row
        const totalAmount = customSales.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
        // Place 'Total' text one cell before the total amount, in the same row
        rows.push(['', 'Total', totalAmount, '', '', '']);
        let csvContent = rows.map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales_report_${dateRange.from}_to_${dateRange.to}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => setDownloading(false), 1000);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1">SALES OVERVIEW</h4>
                            <p className="text-muted p-0 m-0">Quick snapshot and export of your sales performance</p>
                        </div>
                    </div>
                </div>
                <AdminPageLoader message="Loading sales data..." />
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1">SALES OVERVIEW</h4>
                            <p className="text-muted p-0 m-0">Quick snapshot and export of your sales performance</p>
                        </div>
                    </div>
                </div>
                {/* Sales Summary */}
                <div className="row dashboard-stats-row mb-4">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card dashboard-stats-card">
                            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
                                <span className="stats-icon-wrapper" style={{ background: 'rgba(46, 125, 50, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaRupeeSign style={{ fontSize: 20, color: '#2e7d32' }} />
                                </span>
                                <div>
                                    <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Today's Sales</div>
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#2e7d32', lineHeight: 1.2 }}>{formatCurrency(salesSummary.todaySales || 0)}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesSummary.todayOrders || 0} orders</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card dashboard-stats-card">
                            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
                                <span className="stats-icon-wrapper" style={{ background: 'rgba(21, 101, 192, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaCalendarAlt style={{ fontSize: 20, color: '#1565c0' }} />
                                </span>
                                <div>
                                    <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>This Week</div>
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#1565c0', lineHeight: 1.2 }}>{formatCurrency(salesSummary.weekSales || 0)}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesSummary.weekOrders || 0} orders</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card dashboard-stats-card">
                            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
                                <span className="stats-icon-wrapper" style={{ background: 'rgba(0, 131, 143, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaChartLine style={{ fontSize: 20, color: '#00838f' }} />
                                </span>
                                <div>
                                    <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>This Month</div>
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#00838f', lineHeight: 1.2 }}>{formatCurrency(salesSummary.monthSales || 0)}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesSummary.monthOrders || 0} orders</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card dashboard-stats-card">
                            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
                                <span className="stats-icon-wrapper" style={{ background: 'rgba(239, 108, 0, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaRupeeSign style={{ fontSize: 20, color: '#ef6c00' }} />
                                </span>
                                <div>
                                    <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Sales</div>
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#ef6c00', lineHeight: 1.2 }}>{formatCurrency(salesSummary.totalSales || 0)}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesSummary.totalOrders || 0} orders</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Date Range Filter and Download — cloned from Products page */}
                <div className="reports-control-panel">
                    <div className="reports-date-input-group">
                        <span className="reports-date-label">From:</span>
                        <input 
                            type="date" 
                            name="from" 
                            value={dateRange.from} 
                            onChange={handleDateChange} 
                            className="form-control form-control-sm reports-date-input"
                        />
                    </div>
                    <div className="reports-date-input-group">
                        <span className="reports-date-label">To:</span>
                        <input 
                            type="date" 
                            name="to" 
                            value={dateRange.to} 
                            onChange={handleDateChange} 
                            className="form-control form-control-sm reports-date-input"
                        />
                    </div>
                    
                    <button
                        onClick={fetchCustomSales}
                        disabled={!dateRange.from || !dateRange.to || loading}
                        className="btn btn-reset-square"
                        title="Search"
                        style={{
                            cursor: (!dateRange.from || !dateRange.to || loading) ? 'not-allowed' : 'pointer',
                            opacity: (!dateRange.from || !dateRange.to || loading) ? 0.6 : 1
                        }}
                    >
                        <FaSearch style={{ fontSize: '14px' }} />
                    </button>

                    <button
                        onClick={handleReset}
                        className="btn btn-reset-square"
                        title="Reset Filters"
                        style={{
                            cursor: 'pointer'
                        }}
                    >
                        <FaUndo style={{ fontSize: '14px' }} />
                    </button>

                    <button
                        onClick={handleDownloadCSV}
                        disabled={customSales.length === 0 || downloading}
                        className="btn btn-reset-square"
                        title={downloading ? 'Downloading...' : 'Download CSV'}
                        style={{
                            cursor: (customSales.length === 0 || downloading) ? 'not-allowed' : 'pointer',
                            opacity: (customSales.length === 0 || downloading) ? 0.6 : 1
                        }}
                    >
                        <FaDownload style={{ fontSize: '14px' }} />
                    </button>
                </div>

                {/* Custom Sales Table */}
                <div className="row orders-table-row">
                    <div className="col-12">
                        {customSales.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover orders-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '15%', minWidth: '120px' }}>Order ID</th>
                                            <th style={{ width: '25%', minWidth: '140px' }}>Date</th>
                                            <th style={{ width: '15%', minWidth: '120px' }}>Amount</th>
                                            <th style={{ width: '15%', minWidth: '100px' }}>Status</th>
                                            <th style={{ width: '15%', minWidth: '120px' }}>Mobile</th>
                                            <th style={{ width: '15%', minWidth: '120px' }}>Paid Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customSales.map((order) => (
                                            <tr key={order._id}>
                                                <td>
                                                    <Link to={`/dashboard/admin/order-details/${order._id}`} style={{ textDecoration: 'none', fontWeight: '700', color: '#1a237e' }}>
                                                        BEST-{order._id.slice(-5)}
                                                    </Link>
                                                </td>
                                                <td style={{ color: '#4b5563', fontSize: '13px' }}>{new Date(order.createdAt).toLocaleString('en-IN')}</td>
                                                <td style={{ color: '#2e7d32', fontWeight: 700 }}>{formatCurrency(order.totalAmount)}</td>
                                                <td>
                                                    <span style={{ 
                                                        background: '#e2f0d9', 
                                                        color: '#385723', 
                                                        borderRadius: '4px', 
                                                        padding: '2px 8px', 
                                                        fontWeight: '600', 
                                                        fontSize: '11px',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td style={{ color: '#4b5563', fontSize: '13px' }}>{order.mobile}</td>
                                                <td>
                                                    <span style={{
                                                        fontWeight: '600',
                                                        fontSize: '13px',
                                                        color: order.paymentStatus === 'paid' ? '#2e7d32' : '#c62828'
                                                    }}>
                                                        {order.paymentStatus === 'paid'
                                                            ? (order.paymentMethod === 'paynow' ? 'ONLINE' : order.paymentMethod === 'cod' ? 'OFFLINE' : order.paymentMethod.toUpperCase())
                                                            : order.paymentStatus.toUpperCase()}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Total row */}
                                        <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                                            <td style={{ color: '#1a237e' }}>TOTAL</td>
                                            <td></td>
                                            <td style={{ color: '#2e7d32', fontWeight: 700 }}>{formatCurrency(customSales.reduce((sum, order) => sum + (order.totalAmount || 0), 0))}</td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="card border-0 shadow-sm rounded-0">
                                <div className="card-body text-center py-5">
                                    <FaChartLine className="text-muted fs-1 mb-3" />
                                    <h5 className="text-muted">
                                        {dateRange.from && dateRange.to ? 'No sales found in this date range.' : 'Select a date range and search to view sales reports.'}
                                    </h5>
                                </div>
                            </div>
                        )}
                    </div>
                </div>


            </div>
        </Fragment>
    );
};

export default AdminReportPage;